"use client";

import AiLabel from "@/components/ai-label";
import { AlgoliaAPI } from "@/services/algolia/types";
import { Content } from "@/services/content/types";
import { formatTime, localDate } from "@/utils/date";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRef, useState } from "react";

type PlayTableauProps = {
  shows?: Content.PlayShow[];
  episodes?: AlgoliaAPI.PlayEpisode[];
  fetchEpisodes?: (date: string) => void;
};

const PlayTableau = ({
  shows,
  episodes: initalEpoisodes,
  fetchEpisodes,
}: PlayTableauProps) => {
  const createTimestamp = (offset: number) => {
    const date = localDate();
    date.setDate(date.getDate() + offset);
    return date.setHours(0, 0, 0, 0).toString();
  };

  const filters = [
    { label: "Igår", date: createTimestamp(-1) },
    { label: "Idag", date: createTimestamp(0) },
    { label: "Imorgon", date: createTimestamp(1) },
  ];

  const [activeFilter, setActiveFilter] = useState(filters[1]);
  const [episodes, setEpisodes] = useState(initalEpoisodes);

  const changeInterval = async (filter: (typeof filters)[0]) => {
    setActiveFilter(filter);
    const res = await fetchEpisodes?.(filter.date);
    if (res) {
      setEpisodes(res);
    }
  };

  const timeStampToDate = (timestamp: string) => {
    const date = new Date(parseInt(timestamp));
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  };

  return (
    <div className="container">
      <div className="mt-8 mb-20">
        <div className="flex items-center sm:space-x-4 overflow-x-auto hide-scrollbar bg-sand-600 rounded-sm p-4">
          <span>Visar:</span>
          <div
            role="listbox"
            className="flex space-x-4 text-sm [&>:first-child]:hidden sm:[&>:first-child]:block"
          >
            {filters.map((filter, i) => {
              const isSelected = activeFilter.label === filter.label;

              return (
                <button
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => changeInterval(filter)}
                  key={i}
                  className={classNames(
                    "hover:bg-static-white hover:text-static-black px-3 py-2 whitespace-nowrap rounded-sm",
                    isSelected ? "bg-static-white text-static-black" : ""
                  )}
                >
                  {filter.label}
                </button>
              );
            })}

            <div className="inline-flex">
              <DatePicker
                isActive={activeFilter.label === "custom"}
                onChange={(selectedDate: string) => {
                  const date = new Date(selectedDate);
                  changeInterval({
                    label: "custom",
                    date: date.setHours(0, 0, 0, 0).toString(),
                  });
                }}
              />
              {activeFilter.label === "custom" && (
                <span className="ml-1 self-center shrink-0">
                  {timeStampToDate(activeFilter.date)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-8 mt-12 lg:mt-20">
          <div className="col-span-12 md:col-span-9 space-y-12">
            {episodes?.map(playItem)}
          </div>

          <div className="col-span-12 md:col-span-3 md:col-start-10 space-y-4 mt-8 md:mt-0">
            <h2 className="font-noto-sans uppercase text-lg">Program</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-1 gap-4">
              {shows?.map(
                (show) =>
                  show.fields.image?.fields.file && (
                    <Link
                      href={`/program/${show.fields.slug}`}
                      className="relative block"
                      key={show.sys.id}
                    >
                      <Image
                        alt={show.fields.image?.fields.alt || ""}
                        src={show.fields.image?.fields?.file?.url}
                        width={960}
                        height={540}
                      />
                      <AiLabel image={show.fields.image} />
                    </Link>
                  )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DatePicker = (props: { isActive: boolean; onChange: any }) => {
  const datePickerRef = useRef<HTMLInputElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <button
      role="option"
      aria-expanded={isExpanded}
      aria-selected={props.isActive}
      onBlur={() => setIsExpanded(false)}
      onClick={() => {
        datePickerRef.current?.showPicker();
        setIsExpanded(true);
      }}
      className={classNames(
        "hover:bg-static-white hover:text-static-black px-3 py-2 whitespace-nowrap rounded-sm relative",
        {
          "bg-static-white text-static-black": props.isActive,
          "bg-sand-500": !props.isActive,
        }
      )}
    >
      {props.isActive ? "Valt" : " Välj"} datum
      <input
        tabIndex={-1}
        ref={datePickerRef}
        id="default-datepicker"
        type="date"
        className="h-0 w-0 absolute bottom-0 left-0"
        placeholder="Select date"
        onChange={() => {
          props.onChange(datePickerRef.current?.value);
          setIsExpanded(false);
        }}
      />
    </button>
  );
};

const playItem = (episode: AlgoliaAPI.PlayEpisode, i: number) => {
  const formatDate = (date?: number) => {
    if (!date) return "";
    const unixToTimeStamps = date * 1000;
    return formatTime(unixToTimeStamps, "sv-SE", "Europe/Stockholm");
  };

  const episodeHasStarted =
    episode.broadcastStartsAt <= Math.floor(localDate().getTime() / 1000);

  return (
    <div className="flex gap-4 lg:gap-8" key={episode.id}>
      <div className="w-[65px] shrink-0">
        <span>{formatDate(episode.broadcastStartsAt)}</span>
      </div>
      <article>
        <Link href={`/play/${episode?.slug || "#"}`} className="space-y-1">
          <div className="text-static-white text-sm font-bold">
            {episodeHasStarted ? episode.playShow?.name : ""}
          </div>
          <h2 className="font-noto font-semibold text-2xl">
            {episodeHasStarted ? episode.title : episode.playShow.name}
          </h2>
          <span className="block font-normal text-lg">
            {episode.participants?.length > 0 && (
              <>
                <span className="font-bold">Medverkande:</span>{" "}
                <ul className="inline">
                  {episode.participants.map((name, i) => (
                    <li key={i} className="inline">
                      {name}
                      {i < episode.participants.length - 1 ? ", " : ""}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </span>
        </Link>
      </article>
    </div>
  );
};

export default PlayTableau;
